import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRightIcon, GiftIcon, StoreIcon, TruckIcon } from 'lucide-react';
import { products } from '../data/products';
import { FREE_SHIPPING_THRESHOLD, store } from '../data/store';
import { ExhibitionProgress } from '../components/creator/ExhibitionProgress';
import { AddToCartPanel } from '../components/product/AddToCartPanel';
import { ProductGallery } from '../components/product/ProductGallery';
import { ProductGrid } from '../components/product/ProductGrid';
import { AccordionItem } from '../components/ui/Accordion';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { SectionHeading } from '../components/ui/SectionHeading';
import { getCategory, getCreator, getProduct, getProductsByCreator } from '../utils/catalog';
import { formatPrice } from '../utils/currency';
import { daysLeft, formatDate, isLeavingSoon } from '../utils/exhibition';
import { NotFound } from './NotFound';
export function ProductDetail() {
  const {
    productId
  } = useParams();
  const product = productId ? getProduct(productId) : undefined;
  const creator = product ? getCreator(product.creatorId) : undefined;
  if (!product || !creator) return <NotFound data-id="element-896" data-mp="3awni8k4htcs8" />;
  const category = getCategory(product.category);
  const sameCreator = getProductsByCreator(creator.id).filter(p => p.id !== product.id);
  const similar = products.filter(p => p.category === product.category && p.creatorId !== creator.id).slice(0, 4);
  const leaving = isLeavingSoon(creator);
  const left = daysLeft(creator);
  return <>
      <div className="mx-auto max-w-[1440px] px-5 pb-16 pt-6 md:px-8 md:pt-10" data-id="element-897" data-mp="3tmlxplame85w">
        <Breadcrumbs items={[{
        label: 'Boutique',
        to: '/boutique'
      }, ...(category ? [{
        label: category.name,
        to: `/boutique?categorie=${category.id}`
      }] : []), {
        label: product.name
      }]} data-id="element-898" data-mp="3topyhud82r1y" />
        <div className="mt-6 grid gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-899" data-mp="2n72vrr94rxe7">
          <div className="lg:col-span-7" data-id="element-900" data-mp="234ouei56exlh">
            <ProductGallery product={product} data-id="element-901" data-mp="233nk4fvzif4t" />
          </div>
          <div className="self-start lg:sticky lg:top-28 lg:col-span-5" data-id="element-902" data-mp="1z2bontqfizop">
            <Link to={`/createurs/${creator.id}`} className="text-sm text-muted transition-colors duration-150 hover:text-clay" data-id="element-903" data-mp="1ubv9o56yn608">
              {creator.name} · {creator.maker}
            </Link>
            <h1 className="mt-2 font-display text-4xl leading-[1.02] md:text-5xl" data-id="element-904" data-mp="1kdnwlxfsl83i">{product.name}</h1>
            <p className="mt-3 text-xl tabular-nums" data-id="element-905" data-mp="1fekq08k5yttf">{formatPrice(product.price)}</p>

            <div className={`mt-6 rounded-md p-4 ${leaving ? 'bg-clay/10' : 'bg-card'}`} data-id="element-906" data-mp="1fgxi3ejmykk7">
              <p className="mb-3 text-sm" data-id="element-907" data-mp="1bf3kjf4qlwo9">
                {leaving ? <>
                    <span className="font-medium text-clay" data-id="element-908" data-mp="16fzc4qpn02yz">
                      Plus que {left} jour{left > 1 ? 's' : ''}.
                    </span>{' '}
                    {creator.maker} quitte la vitrine le {formatDate(creator.exhibitionEnd)} ; cette pièce partira avec elle·lui.
                  </> : <>
                    En vitrine jusqu’au <span className="font-medium" data-id="element-909" data-mp="3d98cn3i56qtt">{formatDate(creator.exhibitionEnd)}</span>, fin de
                    l’exposition de {creator.name}.
                  </>}
              </p>
              <ExhibitionProgress creator={creator} variant="compact" data-id="element-910" data-mp="3mzta2nm2xpb5" />
            </div>

            <p className="mt-6 text-[15px] leading-relaxed text-muted" data-id="element-911" data-mp="2pdvvv2eqg650">{product.description}</p>

            <div className="mt-8" data-id="element-912" data-mp="2ucgmca46nbg9">
              <AddToCartPanel product={product} data-id="element-913" data-mp="2udrsggl3ovaj" />
            </div>

            <ul className="mt-8 space-y-3 text-sm" data-id="element-914" data-mp="2z9yzkcp8gcu2">
              <li className="flex gap-3" data-id="element-915" data-mp="33c3pb0ck8pbf">
                <StoreIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden data-id="element-916" data-mp="33d4zl2lr57s3" />
                <span data-id="element-917" data-mp="elaw659gjfjm">Retrait gratuit en boutique sous 24 h, {store.address}</span>
              </li>
              <li className="flex gap-3" data-id="element-918" data-mp="ilcy8fj3uazf">
                <TruckIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden data-id="element-919" data-mp="ikbelmo0wg63" />
                <span data-id="element-920" data-mp="h5qro4yrll7z">Livraison offerte dès {FREE_SHIPPING_THRESHOLD} €, expédiée sous 2 jours</span>
              </li>
              <li className="flex gap-3" data-id="element-921" data-mp="c7p71floxdue">
                <GiftIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden data-id="element-922" data-mp="r3od6kp8p3ww" />
                <span data-id="element-923" data-mp="r0sid58ouvco">Emballage cadeau offert sur demande</span>
              </li>
            </ul>

            <div className="mt-8 border-t border-line" data-id="element-924" data-mp="m6eox8dl6yan">
              <AccordionItem title="Détails et entretien" defaultOpen data-id="element-925" data-mp="102ig0st5yqiw">
                <ul className="list-disc space-y-1 pl-4" data-id="element-926" data-mp="v59vilkjmaeh">
                  {product.details.map(d => <li key={d} data-id="element-927" data-mp="19twfj0l47qc1">{d}</li>)}
                </ul>
              </AccordionItem>
              <AccordionItem title="Livraison et retours" data-id="element-928" data-mp="14vl5oputlftt">
                Retrait en boutique gratuit, point relais 3,90 €, Colissimo 5,90 € (offerts dès {FREE_SHIPPING_THRESHOLD} €).
                Vous disposez de 14 jours pour changer d’avis ; les retours sont acceptés en boutique ou par la poste, tant
                que la pièce n’a pas été portée ou utilisée.
              </AccordionItem>
              <AccordionItem title="Fait main, donc unique" data-id="element-929" data-mp="22etdxfvyrew8">
                Chaque pièce est façonnée à la main par {creator.maker}. De légères variations de forme, de couleur ou de
                texture font partie de son caractère et ne sont pas des défauts.
              </AccordionItem>
            </div>
          </div>
        </div>
      </div>

      <section className="bg-shell" aria-labelledby="createur-title" data-id="element-930" data-mp="1jh8n439n268z">
        <div className="mx-auto grid max-w-[1440px] gap-8 px-5 py-16 md:grid-cols-12 md:gap-12 md:px-8 md:py-20" data-id="element-931" data-mp="1jijsu8mn9o2q">
          <div className="md:col-span-4" data-id="element-932" data-mp="15o0vwcpgzcrt">
            <div className="aspect-[4/5] overflow-hidden rounded-sm" data-id="element-933" data-mp="19qntyvcqhtxg">
              <img src={creator.image} alt={`L’univers de ${creator.name}`} className="h-full w-full object-cover" loading="lazy" data-id="element-934" data-mp="vro1fcm3ouw0" />
            </div>
          </div>
          <div className="flex flex-col justify-center md:col-span-8 lg:col-span-6" data-id="element-935" data-mp="10pzdzv3s5jhx">
            <p className="text-sm text-muted" data-id="element-936" data-mp="32gq103h4zqcc">
              {creator.craft} · {creator.city}
            </p>
            <h2 id="createur-title" className="mt-2 font-display text-4xl md:text-5xl" data-id="element-937" data-mp="2xnn53oow04sz">
              {creator.name}
            </h2>
            <blockquote className="mt-6 font-display text-2xl italic leading-snug text-clay md:text-3xl" data-id="element-938" data-mp="2ooibmws6n1no">« {creator.quote} »</blockquote>
            <p className="mt-6 leading-relaxed text-muted" data-id="element-939" data-mp="3ldraa0hq4cm3">{creator.bio}</p>
            <Link to={`/createurs/${creator.id}`} className="group mt-8 inline-flex items-center gap-2 self-start text-sm font-medium" data-id="element-940" data-mp="3hdgwi1nowhwo">
              <span className="border-b border-ink/30 pb-0.5 group-hover:border-ink" data-id="element-941" data-mp="3cct2r882tmyr">Découvrir {creator.name}</span>
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-942" data-mp="37ernyzhoo9t3" />
            </Link>
          </div>
        </div>
      </section>

      {sameCreator.length > 0 && <section className="mx-auto max-w-[1440px] px-5 pt-16 md:px-8 md:pt-20" aria-labelledby="meme-createur" data-id="element-943" data-mp="1w2793gl0mqo8">
          <SectionHeading id="meme-createur" title={<>Du même créateur</>} data-id="element-944" data-mp="1qur2t83lguga" />
          <ProductGrid products={sameCreator} data-id="element-945" data-mp="1qswd7ghdoe5v" />
        </section>}

      {similar.length > 0 && <section className="mx-auto max-w-[1440px] px-5 pt-16 md:px-8 md:pt-20" aria-labelledby="similaires" data-id="element-946" data-mp="1vs9021891l8g">
          <SectionHeading id="similaires" title={<>
                Vous aimerez <em className="text-clay" data-id="element-948" data-mp="29l8hi8rldpin">aussi</em>
              </>} link={category ? {
        to: `/boutique?categorie=${category.id}`,
        label: category.name
      } : undefined} data-id="element-947" data-mp="24mfdd6y73av5" />
          <ProductGrid products={similar} data-id="element-949" data-mp="2jjek6sl82zok" />
        </section>}
    </>;
}